import type {ReactElement} from 'react';
import {Button, Size, Type} from '@Components/button';
import Divider from '@Components/divider';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {buyCreditPack} from '@Components/ai-credits-pill/ai-credits-pill.library';
import {isUserPremium, isUserPremiumPlus} from '@Libraries/user.library';
import {AiAgentMode} from '@AiChat/providers/ai-chat-provider.types';
import {useAppSelector} from '@/hooks';
import {AiChatTestId} from '@/test-ids/ai-chat-test-id';
import {getRenewalCreditsText} from '@AiChat/components/ai-chat-credits-dropdown/ai-chat-credits-dropdown.library';
import {CREDITS_LEARN_MORE_URL} from '@AiChat/components/ai-chat-credits-dropdown/ai-chat-credits-dropdown.types';
import {CreditsExpiryBreakdown} from '@AiChat/components/ai-chat-credits-dropdown/components/credits-expiry-breakdown/credits-expiry-breakdown';
import type {CreditsSummaryPopupProps} from './credits-summary-popup.types';
import styles from './credits-summary-popup.module.scss';

export function CreditsSummaryPopup({credits, mode, isMobile, onHoverStart, onHoverEnd, onActionTaken, onCreditsPurchased, onOpenSummaryModal}: CreditsSummaryPopupProps): ReactElement {
  const isPremiumUser = isUserPremium() || isUserPremiumPlus();
  const renewalCredits = useAppSelector((s) => s.teamCredits.renewalCredits);
  const renewalDate = useAppSelector((s) => s.teamCredits.renewalDate);

  const getRenewalText = (): string | null => {
    if (!renewalCredits || !renewalDate) {
      return null;
    }

    return getRenewalCreditsText(renewalCredits, renewalDate);
  };

  const addCredits = (): void => {
    onActionTaken();
    buyCreditPack(onCreditsPurchased);
  };

  const upgrade = (): void => {
    onActionTaken();
    window.PMW.openPremiumPricingModal(false);
  };

  const learnMore = (): void => {
    onActionTaken();
    window.open(CREDITS_LEARN_MORE_URL, '_blank');
  };

  const getUsageNote = (): ReactElement | null => {
    if (mode !== AiAgentMode.AGENT) {
      return null;
    }

    return <Text val={window.i18next.t('pmwjs_ai_agent_credits_vary_by_task')} size={TextSize.XSMALL} className="body-md-bold ct-neutral-secondary _text-nowrap" />;
  };

  const getExpiryIcon = (): ReactElement | null => {
    if (getRenewalText()) {
      return null;
    }

    const icon = (
      <div className={`flexbox ${styles.expiryBreakdownIcon}`}>
        <Icon icon="icon-info" size={IconSize.SIZE_ICON_16} isUsedAsButton ariaLabel={window.i18next.t('pmwjs_ai_credits_details')} />
      </div>
    );

    return <CreditsExpiryBreakdown anchor={icon} isMobile={isMobile} onOpenSummaryModal={onOpenSummaryModal} />;
  };

  const getCreditsRow = (): ReactElement => {
    return (
      <div className={styles.creditsInfoRow}>
        <div className="flex-row-align-center gap-1">
          <Text val={window.i18next.t('pmwjs_ai_credits_remaining')} size={TextSize.XSMALL} className="ct-neutral-secondary body-md-regular" />
          {getExpiryIcon()}
        </div>
        <div className="flex-row-align-end gap-1">
          <Icon icon="icon-flash" size={IconSize.SIZE_ICON_16} className={`ct-neutral-primary ${styles.creditIcon}`} />
          <div className="heading-h3 ct-neutral-primary">{credits.toLocaleString()}</div>
        </div>
        {getUsageNote()}
      </div>
    );
  };

  const getActionButton = (): ReactElement => {
    if (isPremiumUser) {
      return <Button text={window.i18next.t('pmwjs_ai_credits_add_more')} type={Type.PRIMARY} size={Size.SMALL} customClasses="radius-8 flex-1" onClick={addCredits} data-testid={AiChatTestId.ADD_MORE_CREDITS_CTA} />;
    }

    return <Button text={window.i18next.t('pmwjs_ai_credits_upgrade_for_more')} type={Type.PREMIUM} size={Size.SMALL} customClasses="radius-8 flex-1" onClick={upgrade} data-testid={AiChatTestId.UPGRADE_FOR_CREDITS_CTA} />;
  };

  const getRenewalRow = (): ReactElement | null => {
    const renewalText = getRenewalText();

    if (!renewalText) {
      return null;
    }

    const link = (
      <span className={styles.expiryBreakdownAnchor}>
        <Text val={window.i18next.t('pmwjs_learn_more')} size={TextSize.SM} className="ct-brand-primary-link _cursor-pointer" underline />
      </span>
    );

    return (
      <div className={styles.creditsExpiryContainer}>
        <Divider additionalClasses="spacing-m-b-3" />
        <div>
          <Text val={renewalText} size={TextSize.SM} className="ct-neutral-secondary" />
          <CreditsExpiryBreakdown anchor={link} isMobile={isMobile} onOpenSummaryModal={onOpenSummaryModal} />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.popup} onMouseEnter={onHoverStart} onMouseLeave={onHoverEnd}>
      <div className={styles.creditsInfoContainer}>{getCreditsRow()}</div>
      <div className={styles.buttons}>
        {getActionButton()}
        <Text val={window.i18next.t('pmwjs_ai_credits_learn_how')} size={TextSize.XSMALL} bold className="content-link _cursor-pointer" onClick={learnMore} />
      </div>
      {getRenewalRow()}
    </div>
  );
}
